import { IconButton } from "@mui/material";
import { capitalize } from "lodash-comms";
import { forwardRef, PropsWithChildren } from "react";
import { MdClose } from "react-icons/md";
import { IComposeMessageForm } from "~/components/ComposeMessageContext";
import { Tooltip } from "~/components/Tooltip";
import * as ThreadLayout from "~/page-layouts/thread-layout";
import { useControlState } from "~/components/forms/utils";
import { RiGitBranchLine } from "react-icons/ri";
import { BsLockFill } from "react-icons/bs";
import { SwitchCase } from "~/components/SwitchCase";
import { UnreachableCaseError } from "libs/errors";
import { ThreadVisibility } from "libs/schema";
import { useThreadVisibility } from "~/hooks/useThreadVisibility";
import { useThreadSubject } from "~/hooks/useThreadSubject";
import { closeDraftCommand } from "~/utils/common-commands";
import { useShowComposeInfoPanel } from "~/hooks/useShowComposeInfoPanel";
import { ParentComponent } from "~/utils/type-helpers";
import { Link } from "@tanstack/react-router";

export const Header = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    control: IComposeMessageForm;
    parentThreadId?: string;
  }>
>((props, ref) => {
  const threadType = useControlState(() => props.control.rawValue.type, [props.control]);

  const visibility = useControlState(() => props.control.rawValue.visibility, [props.control]);

  const [showInfoPanel] = useShowComposeInfoPanel();

  const { visibility: parentThreadVisibility } = useThreadVisibility(props.parentThreadId);

  const { subject: parentThreadSubject } = useThreadSubject(props.parentThreadId);

  return (
    <ThreadLayout.Header ref={ref} theme={visibility === "PRIVATE" ? "dark" : "light"}>
      <ThreadLayout.ActionPanel fullyCollapse />

      <ThreadLayout.ContentPanel>
        <div className="flex flex-col pt-7 pb-6 px-8 border-l-[3px] border-transparent">
          <div className="flex">
            <h1 className="compose-new-post-header text-2xl">
              New <VisibilityLabel visibility={visibility} />{" "}
              <SwitchCase deps={[threadType, props.parentThreadId]}>
                {() => {
                  switch (threadType) {
                    case "COMMS": {
                      return props.parentThreadId ? <>Branch</> : <>Message</>;
                    }
                    case "EMAIL": {
                      return props.parentThreadId ? <>Email Branch</> : <>Email</>;
                    }
                    default: {
                      throw new UnreachableCaseError(threadType);
                    }
                  }
                }}
              </SwitchCase>
            </h1>

            <span className="flex-1" />

            <div className="flex justify-center items-center">
              <Tooltip side="bottom" content="Close">
                <span className="rounded-full group">
                  {/* Doesn't need to be tabbable because of hotkeys */}
                  <IconButton
                    tabIndex={-1}
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      closeDraftCommand.trigger();
                    }}
                    className="scale-125"
                  >
                    <span className="text-slate-9 group-hover:text-black">
                      <MdClose />
                    </span>
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>

          {parentThreadSubject && props.parentThreadId && (
            <div className="mt-2 flex items-center">
              <RiGitBranchLine size="1.5rem" className="mr-2" />

              {parentThreadVisibility === "PRIVATE" && <BsLockFill size="1.2rem" className="mr-2" />}

              <Link
                to="/threads/$threadId"
                params={{ threadId: props.parentThreadId }}
                className="italic hover:underline truncate"
              >
                {parentThreadSubject}
              </Link>
            </div>
          )}
        </div>
      </ThreadLayout.ContentPanel>

      <div className="flex-1" />
    </ThreadLayout.Header>
  );
});

const VisibilityLabel: ParentComponent<{
  visibility?: null | ThreadVisibility;
}> = (props) => {
  if (!props.visibility) return null;
  return <>{capitalize(props.visibility)}</>;
};
